// User-defined attributes: evaluating FLAG rules (pure; no React).
//
// Flag rules come with the `uda_resolve` answer and are evaluated on the
// client against the record's current values, in the platform's ONE
// expression language (`../expr`, the port of the engine's `expr_eval`), with
// `{form.X}` / `{header.X}` references bound exactly as the engine binds them
// (`er.calc.bind_references`). A flag is a hint, never a gate: an expression
// that cannot be evaluated simply does not raise it. The node test
// (test-script/test_uda_ui.mjs) runs this module against the shared flag
// fixtures that test_uda_model.py runs through Python's expr_eval.

import { DATE_FUNCS, SAFE_FUNCS, bindReferences, evaluate, truthy } from '../expr'
import type { UdaFlag } from './types'

const FUNCS = { ...SAFE_FUNCS, ...DATE_FUNCS } as Record<string, (...a: unknown[]) => unknown>

const norm = (k: string): string => String(k || '').trim().replace(/\s+/g, '_').toLowerCase()

function lookup(bag: Record<string, unknown> | null | undefined, key: string): unknown {
  if (!bag) return undefined
  if (key in bag) return bag[key]
  const n = norm(key)
  if (n in bag) return bag[n]
  const hit = Object.keys(bag).find(k => norm(k) === n)
  return hit !== undefined ? bag[hit] : undefined
}

/** Evaluate one flag expression. `{form.X}` / bare `X` read `values`,
 *  `{header.X}` reads `headerValues`. Returns null when it cannot be
 *  evaluated (a broken rule never raises a flag). */
export function evaluateFlagExpression(
  when: string | null | undefined,
  values: Record<string, unknown> | null | undefined,
  headerValues?: Record<string, unknown> | null,
): boolean | null {
  const src = String(when ?? '').trim()
  if (!src) return null
  try {
    const { src: bound, refs } = bindReferences(src)
    const variables: Record<string, unknown> = {}
    refs.forEach((ref, i) => {
      const dot = ref.indexOf('.')
      const scope = dot >= 0 ? ref.slice(0, dot).toLowerCase() : ''
      const name = dot >= 0 ? ref.slice(dot + 1) : ref
      const v = scope === 'header' ? lookup(headerValues, name)
        : scope === 'form' ? lookup(values, name)
          : lookup(values, ref)
      variables[`_r${i}`] = v === undefined ? null : v
    })
    const out = evaluate(bound, {
      variables,
      functions: FUNCS,
      resolveName: (n) => {
        const v = lookup(values, n)
        return v === undefined ? null : v
      },
    })
    return truthy(out)
  } catch (err) {
    if (typeof console !== 'undefined') console.debug('flag rule not evaluated:', src, err)
    return null
  }
}

/** The flags raised on a record (in declaration order). `place` keeps only
 *  the flags that show there (`header_badge` / `list_column` / `line_row`). */
export function evaluateFlags(
  flags: readonly UdaFlag[] | null | undefined,
  values: Record<string, unknown> | null | undefined,
  opts: { headerValues?: Record<string, unknown> | null; place?: string } = {},
): UdaFlag[] {
  const out: UdaFlag[] = []
  for (const f of flags || []) {
    if (!f || f.hidden) continue
    if (opts.place && !(f.show_on || []).includes(opts.place)) continue
    if (evaluateFlagExpression(f.when, values, opts.headerValues) === true) out.push(f)
  }
  return out
}

